import React, {useMemo} from "react";
import useAccessLevels from "../../../../hooks/use-access-levels";
import DraggablePopover from "../index";
import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import {CloseOutlined, ControlCamera} from "@mui/icons-material";
import Grid from "@mui/material/Unstable_Grid2";
import {ReactComponent as SaveIcon} from "../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../assets/images/save-rollback/cancel.svg";

/**
 * @type {React.FC<React.PropsWithChildren<{
 *     filters?: any[],
 *     title: string,
 *     wrapperProps?: Partial<import('@mui/material').BoxProps>,
 *     buttonProps?: Partial<import('@mui/material').ButtonProps>,
 *     iconProps?: Partial<import('@mui/material').SvgIconProps>,
 * }>>}
 */
const LookupAppliedFiltersPopover = ({
                                         filters,
                                         title,
                                         children,
                                         wrapperProps,
                                         buttonProps,
                                         iconProps,
                                         cancelSelector,
                                     }) => {
    const operatorAccessLevels = useAccessLevels();
    const canRenderPopover = useMemo(() => operatorAccessLevels.packages.dashboard.lookupAnalytics && !!filters.length, [filters.length, operatorAccessLevels.packages.dashboard.lookupAnalytics]);

    return (
        <>
            <DraggablePopover
                popupId={`applied-filters-of-${title}`}
                canRenderPopover={canRenderPopover}
                wrapperProps={wrapperProps}
                buttonProps={buttonProps}
                iconProps={iconProps}
                popoverElement={<LookupAppliedFilters filters={filters} title={title}/>}
                width={500}
                height={500}
                draggableProps={{
                    cancel: `.draggable-popover-cancel ${cancelSelector ? `, ${cancelSelector}` : ""}`
                }}
                popperProps={{
                    slotProps: {
                        root: {
                            className: "lookup-applied-filters-popover-class",
                        },
                    },
                }}
            >
                {children}
            </DraggablePopover>
        </>
    );
}


const LookupAppliedFilters = ({close, filters, title, width, height}) => {

    return (
        <>
            <Box
                className={'d-flex flex-column'}
                sx={{
                    width: `min(90vw, ${width}px)`,
                    maxHeight: `min(90vh, ${height}px)`,
                    overflowY: 'auto',
                    position: 'relative',
                    gap: 2,
                    padding: 1,
                }}
            >
                <Box sx={{
                    position: 'sticky',
                    top: -8,
                    zIndex: 3,
                    left: 0,
                    backgroundColor: 'background.paper',
                    padding: 1,
                    paddingInlineEnd: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Tooltip title={"You can move this popover through the panel."} placement={'right'} followCursor>
                            <ControlCamera color={'primary'}/>
                        </Tooltip>
                        <IconButton onClick={close} color={'error'}>
                            <CloseOutlined/>
                        </IconButton>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography component={'div'} variant={'body2'} fontWeight={600}>
                            {title}
                        </Typography>
                    </Box>
                    <Grid container spacing={1} sx={{width: '100%', padding: 1, paddingBlock: 2, paddingBlockEnd: 0}}>
                        <Grid item xs>
                            <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>Name</Typography>
                        </Grid>
                        <Grid item xs>
                            <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>Value</Typography>
                        </Grid>
                        <Grid item xs={'auto'}>
                            <Box sx={{width: 50, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '100%'}}>
                                <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>Sorted</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={'auto'}>
                            <Box sx={{width: 55, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '100%'}}>
                                <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>Sort Desc</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={'auto'}>
                            <Box sx={{width: 50, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '100%'}}>
                                <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>Filtered</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%'}}>
                    {
                        filters.map((filter) => (
                            <Grid container spacing={1} key={filter.id ?? filter.industryIndexStructureId} sx={{width: '100%', padding: 1, paddingBlock: 1.5}}>
                                <Grid item xs>
                                    <Typography variant={'body2'}>{filter.name ?? ""}</Typography>
                                </Grid>
                                <Grid item xs>
                                    <Typography variant={'body2'}>{filter.value ?? ""}</Typography>
                                </Grid>
                                <Grid item xs={'auto'}>
                                    <Box sx={{width: 50, textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                                        {filter.isForSorting ? <SaveIcon className={'save-icon-new'}/> : <CancelIcon className={'cancel-icon-new'}/>}
                                    </Box>
                                </Grid>
                                <Grid item xs={'auto'}>
                                    <Box sx={{width: 55, textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                                        {filter.isDesc ? <SaveIcon className={'save-icon-new'}/> : <CancelIcon className={'cancel-icon-new'}/>}
                                    </Box>
                                </Grid>
                                <Grid item xs={'auto'}>
                                    <Box sx={{width: 50, textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                                        {filter.isForFiltering ? <SaveIcon className={'save-icon-new'}/> : <CancelIcon className={'cancel-icon-new'}/>}
                                    </Box>
                                </Grid>
                            </Grid>
                        ))
                    }
                </Box>
            </Box>
        </>
    );
}

export default LookupAppliedFiltersPopover;